function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import isEqual from 'lodash/isEqual';
import { produce } from 'immer';
import { VNode } from 'snabbdom';
import { jsx } from 'panel';
import { SelectEl } from 'common/components/select';
import { FeatureGate, ProjectFeatureFlag, ReportName } from 'common/report/constants';
import { ReportVisualization } from 'common/report/report-manager/visualization';
import { isPeopleTimeSeries } from 'common/report/insights/util';
import { getSelectedSegments } from 'common/report/insights/util/segments';
import { getDateRange } from 'common/report/insights/util/time';
import { DisplayOptions, InsightsQueryError, InsightsQueryResults } from 'common/report/insights/util/types';
import { getOwnAttr } from 'common/util';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { ChartHeaderUpdateDetail } from 'common/widgets/chart-header';
import { OrderedStandardTimeUnits, TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { ChartHeaderAttrs, ChartHeaderParams } from 'common/widgets/chart-header/types';
import { propagateEvent } from 'common/util/dom';
import { SegmentFilterDataTableJsxProps } from 'common/charts/misc/segment-filter-data-table';
import { QueryReason } from 'common/report/util/query-tracking/types';
import { ColumnResizeEvent, SortChangeEvent } from 'common/components/data-table/types';
import { DataSortMenuEl } from 'common/widgets/data-sort-menu';
import { ReportStatus } from 'common/widgets/warning-hero/constants';
import { canUseViewN } from 'common/components/data-table/utils';
import { ChartType, InsightsBookmarkParams, SavedLegend, SortBy, SortConfig, ValueRepresentationType } from 'common/types/reports/bookmark';
import { VIEW_N_DEFAULT_LIMIT } from 'common/components/data-table/constants';
import { SelectTheme } from 'common/components/select/constants';
import { TableToggleableMetrics } from 'common/report/funnels/models/constants';
import { TABLE_COLUMN_SELECTOR_DISPLAY_NAME, TABLE_COLUMN_SELECTOR_TOOLTIPS } from 'common/report/funnels/models/constants';
import { DisplaySize, Placement } from 'common/components/tooltip/types';
import { getFormattedRowData, hasRetentionData } from 'common/report/retention/util/parsing';
import { BookmarkType } from 'common/report/queries/app-api/bookmark-client';
import { RequestOdaDetail } from 'common/report/report-manager/types';
import { ODAControlEl } from 'common/widgets/oda-control';
import { Retention } from 'common/report/retention/models/types';
import { getLocation } from 'common/util/url';
import { RetentionBookmarkController } from 'common/report/retention/bookmark-controller';
import { Report } from '../models/report';
import { hasEventBreakdown, hasEventComparison, hasFunnelConversionMetric, hasGroupByCohort, hasRetentionFrequency, hasRetentionRateMetric, isRetentionOnlyInsightsBookmark } from '../models/utils';
import { InsightsRetentionTableEl } from '../charts/insights-retention-table-chart';
import { TimeClause } from '../models/time-clause';
import { InsightsChartDisplayEl } from './charts/chart-display';
import { bookmarkUpdatesFromHeaderUpdate, correctDisplayOptions, getChartTypeSectionOverrides, getLeafLevelSegments, getSelectedChartType, getSubDayGranularityDisabledMessage, getTimeComparisonDisabledReason, getTimeComparisonDisabledReasonMessage, getTimeUnitSectionOverrides, shouldRequeryDisplayOptions } from './util';
import { getDataTableTrackName } from './charts/chart-display/utils';
export class InsightsVisualization extends ReportVisualization<InsightsBookmarkParams, InsightsQueryResults, InsightsQueryError> {
  get reportName() {
    if (this.attr("insights-report-mode") === BookmarkType.Funnels) {
      return ReportName.Funnels;
    }
    if (this.attr("insights-report-mode") === BookmarkType.Retention) {
      return ReportName.Retention;
    }
    return ReportName.Insights;
  }
  get defaultState() {
    return {
      // TODO remove this once table column widths are persisted to bookmark
      tableChartColumnWidthOverrides: {}
    };
  }
  getProject() {
    var _this$getContext$get;
    return (_this$getContext$get = this.getContext("currentProject").get()) === null || _this$getContext$get === void 0 ? void 0 : _this$getContext$get.value;
  }
  hasChartCustomization() {
    var _this$getProject;
    return (_this$getProject = this.getProject()) === null || _this$getProject === void 0 ? void 0 : _this$getProject.hasFeatureFlag(ProjectFeatureFlag.ChartCustomization);
  }
  chartDisplay() {
    return <InsightsChartDisplayEl attrs={{
      'resizing-layout': this.attr("show-resizing-layout"),
      'rca-source-id': this.attr("rca-source-id"),
      elref: "chartDisplay",
      'can-use-view-n': canUseViewN(),
      'has-chart-date-range': this.showDateRangeSelector()
    }} props={{
      bookmarkParams: this.bookmarkParams,
      queryResults: this.results,
      queryError: null,
      dataGroups: this.dataGroups,
      tableChartColumnWidthOverrides: this.state.tableChartColumnWidthOverrides
    }} on={{
      'export-csv': ev => propagateEvent(this, ev),
      update: _ref => {
        let {
          detail
        } = _ref;
        let requery = true;

        // chart updates will not have changes alongside sorting or columnWidths,
        // so safe to assume we don't have to query when present.
        const {
          sorting,
          columnWidths
        } = detail.delta;
        if (sorting || columnWidths) {
          requery = false;
        }
        this.dispatchUpdate({
          params: _objectSpread(_objectSpread({}, this.bookmarkParams), detail.delta),
          requery
        });
      },
      columnResize: ev => {
        this.handleDataTableColumnResize(ev);
      }
    }} />;
  }

  // override data table template for custom retention table
  dataTableTemplate(): VNode {
    const chartType = this.bookmarkParams.displayOptions.chartType;
    if (isRetentionOnlyInsightsBookmark(this.bookmarkParams) && chartType === ChartType.RetentionCurve) {
      var _this$project, _this$project2;
      const queryResults = this.results as unknown as InsightsQueryResults<Retention>;
      const bookmarkController = new RetentionBookmarkController(this.bookmarkParams, queryResults);
      const incompleteBuckets = queryResults.meta.report_sections.show[0].incomplete_buckets;
      const segments = bookmarkController.getSegments();
      const {
        data
      } = bookmarkController.flattenData({
        queryResults,
        segments,
        i18n: this.getContext("i18n")
      });
      const tableData = getFormattedRowData({
        data,
        incompleteBuckets,
        segmentGroups: segments,
        timezone: (_this$project = this.project) === null || _this$project === void 0 ? void 0 : _this$project.timezone,
        metricKeyLookup: bookmarkController.metricKeyLookup
      });
      const rowLimit = this.attr("is-download-mode") ? 30 : 255;
      const rowData = tableData.slice(0, rowLimit);
      const tableHasData = hasRetentionData(tableData);
      if (!tableHasData) {
        return null;
      }
      return <InsightsRetentionTableEl sel='.retention-curve-table' params={{
        bookmarkData: {
          bookmark: this.bookmarkParams,
          queryResults
        },
        onExpandedSegmentsChange: (expandedSegments: string[]) => this.handleExpandedSegmentsChange(expandedSegments),
        onSortChange: (sorting: SortConfig) => {
          this.dispatchUpdate({
            params: _objectSpread(_objectSpread({}, this.bookmarkParams), {}, {
              sorting: _objectSpread(_objectSpread({}, this.bookmarkParams.sorting), {}, {
                [ChartType.RetentionCurve]: sorting
              })
            }),
            requery: false
          });
        },
        projectId: this.attr("project-id"),
        projectTimezone: (_this$project2 = this.project) === null || _this$project2 === void 0 ? void 0 : _this$project2.timezone,
        resizingLayout: this.attr("show-resizing-layout"),
        rowData
      }} />;
    }
    return super.dataTableTemplate();
  }
  dataTableFooterTemplate() {
    var _this$project3;
    if (this.attr("is-download-mode")) {
      return null;
    }
    if (this.attr("insights-report-mode") === BookmarkType.Insights) {
      return null;
    }
    if (this.attr("insights-report-mode") === BookmarkType.Funnels) {
      if (hasEventComparison(this.bookmarkParams.sections.show)) {
        return null;
      }
      if (hasEventBreakdown(this.bookmarkParams.sections.group)) {
        return null;
      }
      if (!hasFunnelConversionMetric(this.bookmarkParams)) {
        return null;
      }
    }
    if (this.attr("insights-report-mode") === BookmarkType.Retention) {
      if (hasGroupByCohort(this.bookmarkParams.sections.group)) {
        return null;
      }
      if (hasRetentionFrequency(this.bookmarkParams.sections.show)) {
        return null;
      }
      if (!hasRetentionRateMetric(this.bookmarkParams)) {
        return null;
      }
    }
    return <ODAControlEl sel='.oda-control' attrs={{
      elref: "odaControl",
      description: this.attr("insights-report-mode") === BookmarkType.Retention ? this.t("reports.oda.retention-description") : this.t("reports.oda.funnel-description"),
      'show-upsell': !((_this$project3 = this.project) !== null && _this$project3 !== void 0 && _this$project3.getFeatureGate(FeatureGate.OnDemandAnalysis)),
      'upsell-description': this.attr("insights-report-mode") === BookmarkType.Retention ? this.t("reports.oda.upsell-retention-description") : this.t("reports.oda.upsell-funnel-description")
    }} on={{
      clickAnalyze: () => {
        const bookmarkDateRange = getDateRange(this.bookmarkParams.sections.time.map(time => new TimeClause(time)));
        const detail: RequestOdaDetail = {
          bookmarkDateRange: bookmarkDateRange.getBookmarkFormat(),
          bookmarkUrl: getLocation().hash.slice(1)
        };
        this.dispatchEvent(new CustomEvent<RequestOdaDetail>("request-oda", {
          detail
        }));
      }
    }} />;
  }
  reportSpecificDataTableAttrs(): SegmentFilterDataTableJsxProps[`attrs`] {
    const trackName = getDataTableTrackName(this.bookmarkParams.displayOptions.chartType);
    return {
      'track-name': trackName
    };
  }
  reportSpecificResizingLayoutAttrs() {
    var _this$bookmarkParams;
    if (((_this$bookmarkParams = this.bookmarkParams) === null || _this$bookmarkParams === void 0 || (_this$bookmarkParams = _this$bookmarkParams.displayOptions) === null || _this$bookmarkParams === void 0 ? void 0 : _this$bookmarkParams.chartType) === ChartType.Table) {
      return {
        'disable-content-padding': true
      };
    }
    return {};
  }
  reportSpecificChartExtras() {
    var _this$bookmarkParams2;
    if (((_this$bookmarkParams2 = this.bookmarkParams) === null || _this$bookmarkParams2 === void 0 || (_this$bookmarkParams2 = _this$bookmarkParams2.displayOptions) === null || _this$bookmarkParams2 === void 0 ? void 0 : _this$bookmarkParams2.chartType) === ChartType.FunnelSteps) {
      const items = Object.values(TableToggleableMetrics).map((column: TableToggleableMetrics) => {
        var _this$bookmarkParams$;
        return _objectSpread({
          isSelected: (_this$bookmarkParams$ = this.bookmarkParams.displayOptions.funnelStepsSelectedTableColumns) === null || _this$bookmarkParams$ === void 0 ? void 0 : _this$bookmarkParams$[column],
          label: this.print(TABLE_COLUMN_SELECTOR_DISPLAY_NAME[column]),
          tooltip: {
            text: this.print(TABLE_COLUMN_SELECTOR_TOOLTIPS[column]),
            placement: Placement.Left,
            size: DisplaySize.Small
          },
          value: column
        }, column === TableToggleableMetrics.StatSig ? {
          isDisabled: this.bookmarkParams.sections.group.length === 0
        } // TODO event comparisons?
        : {});
      });
      return <SelectEl attrs={{
        elref: "funnelStepMetricSelector",
        icon: {
          leftIcon: "settings"
        },
        'icon-only': true,
        theme: SelectTheme.Secondary,
        multiple: true,
        'menu-width': 270,
        tooltip: {
          text: this.t("reports.select-table-column.selector-tooltip")
        }
      }} props={{
        sections: [{
          items
        }]
      }} on={{
        change: ev => this.updateFunnelStepColumns(ev)
      }} />;
    }
    return null;
  }
  updateFunnelStepColumns(ev) {
    const column = ev.detail.item.value;
    const defaultTableColumns = Object.fromEntries(Object.values(TableToggleableMetrics).map(key => [key, false])); // TODO set this up when selecting the chart
    const tableColumns = this.bookmarkParams.displayOptions.funnelStepsSelectedTableColumns || defaultTableColumns;
    const funnelStepsSelectedTableColumns = _objectSpread(_objectSpread({}, tableColumns), {}, {
      [column]: !tableColumns[column]
    }) as DisplayOptions[`funnelStepsSelectedTableColumns`];
    this.updateDisplayOptions(_objectSpread(_objectSpread({}, this.bookmarkParams.displayOptions), {}, {
      funnelStepsSelectedTableColumns
    }));
  }
  getBookmarkParamsFromSortChange(ev: SortChangeEvent): InsightsBookmarkParams {
    let {
      chartType
    } = this.bookmarkParams.displayOptions;
    if (chartType === ChartType.FunnelSteps) {
      chartType = ChartType.Bar;
    }
    let sortConfig = ev.detail.sorting;
    if (canUseViewN() && chartType === ChartType.Table && this.bookmarkParams.sorting[chartType].sortBy !== sortConfig.sortBy) {
      // Use default viewNLimits when SortBy changes
      sortConfig = produce(sortConfig, draft => {
        if (draft.sortBy === SortBy.Value) {
          draft.viewNLimit = VIEW_N_DEFAULT_LIMIT;
        } else {
          draft.colSortAttrs.forEach(colSortAttr => {
            colSortAttr.viewNLimit = VIEW_N_DEFAULT_LIMIT;
          });
        }
      });
    }
    const chartTypeForSorting = chartType === ChartType.Column ? ChartType.Line : chartType;
    return _objectSpread(_objectSpread({}, this.bookmarkParams), {}, {
      sorting: _objectSpread(_objectSpread({}, this.bookmarkParams.sorting), {}, {
        [chartTypeForSorting]: sortConfig
      })
    });
  }
  getMultimetricChartTypes() {
    if (this.reportName === ReportName.Funnels) {
      return [ChartType.FunnelSteps, ChartType.Line, ChartType.Bar, ChartType.Metric, ChartType.FunnelTopPaths];
    }
    if (this.reportName === ReportName.Retention) {
      return [ChartType.RetentionCurve, ChartType.Line, ChartType.Metric];
    }
    // default for insights
    return [ChartType.Line, ChartType.StackedLine, ChartType.Bar, ChartType.StackedBar, ChartType.Pie, ChartType.Table, ChartType.Metric, ChartType.Column, ChartType.StackedColumn, ChartType.FunnelSteps, ChartType.RetentionCurve];
  }
  showDateRangeSelector(): boolean {
    const report = Report.fromBookmarkData(this.bookmarkParams);
    return !report.sections.show.isPeopleOnlyQuery() || !isPeopleTimeSeries(this.bookmarkParams.sections);
  }
  reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    // TODO calculate attrs directly from bookmark params
    // for now, use the Report model for parity with Insights app
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const timeComparisonDisabledReason = getTimeComparisonDisabledReason(report); // TODO overrides
    const breakdowns = getSelectedSegments(report.sections.group.clauses);

    // TODO hopefully some of these can be shared among different report types
    return {
      breakdowns,
      'should-divide-chart-types': this.reportName === ReportName.Insights,
      'chart-types': this.getMultimetricChartTypes(),
      'comparison-date-range': report.timeComparison,
      'date-range': getDateRange(report.sections.time.clauses).getAttributes(),
      'date-units': report.showTimeUnitAggregation() && OrderedStandardTimeUnits.filter(value => value !== TimeUnit.Year),
      'disable-sub-day-granularity-message': getSubDayGranularityDisabledMessage(report),
      'selected-chart-type': getSelectedChartType(report),
      'selected-date-unit': report.timeUnit(),
      'show-date-range-selector': this.showDateRangeSelector(),
      'time-comparison-disabled-reason': getTimeComparisonDisabledReasonMessage(report, timeComparisonDisabledReason),
      // lift-specific attrs
      'enable-lift-comparison': true,
      'selected-breakdown-segments': getLeafLevelSegments({
        breakdowns,
        queryResults: this.results,
        report
      }),
      'selected-value-representation': report.displayOptions && report.displayOptions.value || ValueRepresentationType.Absolute,
      'value-representations': [],
      'selected-lift-comparison': report.liftComparison
    };
  }
  reportSpecificHeaderParams(): Partial<ChartHeaderParams> {
    var _this$getProject$time, _this$getProject2, _this$getProject3, _this$getProject4;
    const report = Report.fromBookmarkData(this.bookmarkParams);
    return {
      bookmarkParams: this.bookmarkParams,
      chartTypeOverrides: getChartTypeSectionOverrides({
        bookmarkParams: Report.fromBookmarkData(this.bookmarkParams).toBookmarkData(),
        metricsResource: this.getContext("metrics"),
        behaviorsResource: this.getContext("behaviors")
      }),
      dateUnitOverrides: getTimeUnitSectionOverrides(report, (_this$getProject$time = (_this$getProject2 = this.getProject()) === null || _this$getProject2 === void 0 ? void 0 : _this$getProject2.timezone) !== null && _this$getProject$time !== void 0 ? _this$getProject$time : null, (_this$getProject3 = this.getProject()) === null || _this$getProject3 === void 0 ? void 0 : _this$getProject3.projectAsOfDate, (_this$getProject4 = this.getProject()) === null || _this$getProject4 === void 0 ? void 0 : _this$getProject4.timePeriodSettings),
      liftTypeOverrides: report.getLiftTypeOverrides()
    };
  }
  protected headerChildrenTemplate() {
    var _this$getContext, _this$getContext2, _this$getContext3;
    // Analysis type menu (linear, rolling, etc)
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const sortControls = report.displayOptions.chartType === ChartType.Table && (Boolean(this.attr("status")) === false || this.attr("status") === ReportStatus.Loading) && <>
          <DataSortMenuEl attrs={{
        slot: "report-controls"
      }} params={{
        isLoading: this.attr("status") === ReportStatus.Loading,
        sortConfig: (_this$getContext = this.getContext("segmentFilterController")) === null || _this$getContext === void 0 ? void 0 : _this$getContext.tableSortConfig,
        defaultSortConfig: (_this$getContext2 = this.getContext("segmentFilterController")) === null || _this$getContext2 === void 0 ? void 0 : _this$getContext2.defaultSortConfig,
        columns: (_this$getContext3 = this.getContext("segmentFilterController")) === null || _this$getContext3 === void 0 || (_this$getContext3 = _this$getContext3.getSections()[0]) === null || _this$getContext3 === void 0 ? void 0 : _this$getContext3.tableColumns,
        chartType: this.bookmarkParams.displayOptions.chartType,
        onSortChange: (ev: SortChangeEvent) => {
          this.dispatchUpdate({
            params: this.getBookmarkParamsFromSortChange(ev),
            requery: false
          });
        }
      }} />
        </>;
    const extraControls = this.reportSpecificChartExtras();
    if (sortControls || extraControls) {
      return <>
          {sortControls}
          <div sel='.chart-controls-extra' attrs={{
          slot: "chart-controls"
        }}>
            {extraControls}
          </div>
        </>;
    }
    return null;
  }
  updateDisplayOptions(options: DisplayOptions) {
    const oldDisplayOptions = this.bookmarkParams.displayOptions;
    const newDisplayOptions = correctDisplayOptions(options);
    if (!isEqual(oldDisplayOptions, newDisplayOptions)) {
      const requery = shouldRequeryDisplayOptions(oldDisplayOptions, newDisplayOptions);
      this.dispatchUpdate({
        params: _objectSpread(_objectSpread({}, this.bookmarkParams), {}, {
          displayOptions: newDisplayOptions
        }),
        requery
      });
    }
  }
  handleExpandedSegmentsChange(expandedSegments: string[]) {
    var _this$bookmarkParams$2;
    const savedLegend: SavedLegend = _objectSpread(_objectSpread({}, (_this$bookmarkParams$2 = this.bookmarkParams.legend) === null || _this$bookmarkParams$2 === void 0 ? void 0 : _this$bookmarkParams$2.individualSelectedSegments), {}, {
      expanded: expandedSegments.map(key => ({
        key
      }))
    });
    this.dispatchUpdate({
      params: _objectSpread(_objectSpread({}, this.bookmarkParams), {}, {
        legend: _objectSpread(_objectSpread({}, this.bookmarkParams.legend), {}, {
          individualSelectedSegments: savedLegend
        })
      }),
      requery: false
    });
  }
  handleRollingWindowChange(rollingWindowSize) {
    this.updateDisplayOptions(_objectSpread(_objectSpread({}, this.bookmarkParams.displayOptions), {}, {
      rollingWindowSize
    }));
  }
  handleDataTableColumnResize(ev: ColumnResizeEvent) {
    this.update({
      tableChartColumnWidthOverrides: ev.detail.columnWidthOverrides
    });
  }
  bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail) {
    var _this$getProject$time2, _this$getProject5, _this$getProject6, _this$getProject7;
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const bookmarkUpdates = bookmarkUpdatesFromHeaderUpdate({
      report,
      update,
      hasChangedTimeUnit: this.state.hasChangedTimeUnit,
      projectTimezone: (_this$getProject$time2 = (_this$getProject5 = this.getProject()) === null || _this$getProject5 === void 0 ? void 0 : _this$getProject5.timezone) !== null && _this$getProject$time2 !== void 0 ? _this$getProject$time2 : null,
      projectAsOfDate: (_this$getProject6 = this.getProject()) === null || _this$getProject6 === void 0 ? void 0 : _this$getProject6.projectAsOfDate,
      timePeriodSettings: (_this$getProject7 = this.getProject()) === null || _this$getProject7 === void 0 ? void 0 : _this$getProject7.timePeriodSettings
    });
    if (getOwnAttr(update.delta, "dateUnit")) {
      this.update({
        hasChangedTimeUnit: true
      });
    }
    let requery = true;
    let hasChangedChartType = false;
    if (!isEqual(this.bookmarkParams.displayOptions, bookmarkUpdates.displayOptions)) {
      requery = shouldRequeryDisplayOptions(this.bookmarkParams.displayOptions, bookmarkUpdates.displayOptions);
      const {
        chartType,
        plotStyle
      } = this.bookmarkParams.displayOptions;
      hasChangedChartType = chartType !== bookmarkUpdates.displayOptions.chartType || plotStyle !== bookmarkUpdates.displayOptions.plotStyle;
    }
    const params = _objectSpread(_objectSpread({}, this.bookmarkParams), bookmarkUpdates);
    return {
      params,
      queryReason: QueryReason.QbOtherUpdate,
      requery,
      hasChangedChartType
    };
  }
}
export const InsightsVisualizationEl = defineAndMakeJsxFactory("insights-visualization", InsightsVisualization);
export const InsightsVisualizationReact = panel2react("insights-visualization", InsightsVisualization);